// ENG-4808: reap channel-MCP processes (telegram-channel.js, slack-channel.js,
// direct-chat-channel.js) that are still alive but whose parent claude
// session has exited. Without this, every agent restart leaves a tree of
// long-polling orphans behind — each holds the agent's bot token, eats
// memory, and can race the live MCP for inbound updates.
//
// The decision is "PPID-chain to a live claude" — if a channel-MCP's
// process ancestry reaches a `claude` process with `--name agt-<codeName>`
// in argv, it's live. Otherwise it's an orphan and gets reaped.
//
// This module exposes:
//   • findOrphanChannelMcps()  — pure, takes parsed `ps` rows + returns
//     orphan PIDs. Trivially unit-testable.
//   • reapOrphanChannelMcps()  — side-effecting. Walks ps, then SIGTERMs
//     and after a grace window SIGKILLs any orphans.
//
// macOS / Linux: both support `ps -eo pid,ppid,args` with the format we
// parse here. Windows is not a target for the agent host.

import { execFileSync } from 'node:child_process';

export interface PsRow {
  pid: number;
  ppid: number;
  args: string;
}

/** Match the three channel-MCPs the manager spawns into agent sessions. */
export const CHANNEL_MCP_PATTERN =
  /\.augmented\/_mcp\/(telegram-channel|slack-channel|direct-chat-channel)\.js/;

/** Match a claude process bound to an agent session via `--name agt-<x>`. */
export const CLAUDE_AGENT_PROCESS_PATTERN = /\bclaude\b.*--name\s+agt-[a-z0-9-]+/;

/**
 * Parse `ps -eo pid,ppid,args` output (header + one row per process).
 * Whitespace-tolerant; the `args` column contains the rest of the line
 * after the second whitespace-delimited token.
 */
export function parsePsRows(psOutput: string): PsRow[] {
  const lines = psOutput.split(/\r?\n/);
  const rows: PsRow[] = [];
  // Skip header line(s) — anything where pid isn't a positive integer.
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const match = line.match(/^(\d+)\s+(\d+)\s+(.*)$/);
    if (!match) continue;
    const pid = Number.parseInt(match[1]!, 10);
    const ppid = Number.parseInt(match[2]!, 10);
    if (!Number.isFinite(pid) || !Number.isFinite(ppid)) continue;
    rows.push({ pid, ppid, args: match[3] ?? '' });
  }
  return rows;
}

/**
 * Pure: given a list of `ps` rows, return the PIDs of channel-MCP
 * processes whose ancestry doesn't reach a live `claude --name agt-*`
 * process. "Orphan" = parent claude has exited (so the MCP can no
 * longer deliver `notifications/claude/channel` to anyone).
 *
 * Walks the ppid chain up to `maxDepth` levels (default 8) so an
 * intermediate shell or wrapper doesn't trick us into a false orphan
 * verdict. Stops early on a hit to keep this O(N * maxDepth) at worst.
 */
export function findOrphanChannelMcps(rows: PsRow[], opts?: { maxDepth?: number }): number[] {
  const maxDepth = opts?.maxDepth ?? 8;
  const byPid = new Map<number, PsRow>(rows.map((r) => [r.pid, r]));

  const orphans: number[] = [];
  for (const row of rows) {
    if (!CHANNEL_MCP_PATTERN.test(row.args)) continue;

    // Walk parents looking for a live claude.
    let cur: PsRow | undefined = byPid.get(row.ppid);
    let foundLiveClaude = false;
    for (let depth = 0; depth < maxDepth && cur; depth++) {
      if (CLAUDE_AGENT_PROCESS_PATTERN.test(cur.args)) {
        foundLiveClaude = true;
        break;
      }
      // PID 1 (init) is always reached on Linux for true orphans —
      // stop the walk regardless to bound work.
      if (cur.pid === 1) break;
      cur = byPid.get(cur.ppid);
    }

    if (!foundLiveClaude) orphans.push(row.pid);
  }

  return orphans;
}

/**
 * Run `ps` and reap any orphan channel-MCPs. SIGTERM first, then SIGKILL
 * after `graceMs` for any that didn't exit. Logs each reaped pid so
 * operators see what was cleaned up.
 *
 * Idempotent: calling repeatedly is safe (the second call will find
 * nothing).
 *
 * Returns the list of pids that were sent SIGTERM (informational —
 * tests can use this without mocking process.kill).
 */
export function reapOrphanChannelMcps(args: {
  log: (msg: string) => void;
  graceMs?: number;
  /** Test seam: substitute the `ps` invocation. */
  runPs?: () => string;
  /** Test seam: substitute the kill function. */
  killProcess?: (pid: number, signal: 'SIGTERM' | 'SIGKILL') => void;
  /** Test seam: substitute the still-alive check. */
  isAlive?: (pid: number) => boolean;
}): number[] {
  const { log, graceMs = 5_000 } = args;
  const runPs = args.runPs ?? (() => execFileSync('ps', ['-eo', 'pid,ppid,args'], { encoding: 'utf-8', timeout: 5_000 }));
  const killProcess =
    args.killProcess ??
    ((pid: number, signal: 'SIGTERM' | 'SIGKILL') => {
      try {
        process.kill(pid, signal);
      } catch {
        // Process already exited — fine.
      }
    });
  const isAlive =
    args.isAlive ??
    ((pid: number) => {
      try {
        // Signal 0 doesn't kill anything — just probes whether the pid
        // is reachable. Throws if no such process.
        process.kill(pid, 0);
        return true;
      } catch {
        return false;
      }
    });

  let psOutput: string;
  try {
    psOutput = runPs();
  } catch (err) {
    log(`[orphan-reaper] ps invocation failed: ${(err as Error).message} — skipping reap`);
    return [];
  }

  const rows = parsePsRows(psOutput);
  const orphans = findOrphanChannelMcps(rows);
  if (orphans.length === 0) return [];

  // CodeRabbit on PR #774: surface the script name in log lines so an
  // operator scanning manager.log can immediately tell which channel-MCP
  // was reaped without cross-referencing PIDs against `ps` output. The
  // AC explicitly asked for `script.js (pid N)` format.
  const byPid = new Map<number, PsRow>(rows.map((r) => [r.pid, r]));
  const describeOrphan = (pid: number): string => {
    const scriptMatch = byPid.get(pid)?.args.match(/([^\s/]+\.js)(?:\s|$)/);
    return scriptMatch ? `${scriptMatch[1]} (pid ${pid})` : `pid ${pid}`;
  };

  log(`[orphan-reaper] sending SIGTERM to ${orphans.length} orphan channel-MCP(s): ${orphans.map(describeOrphan).join(', ')}`);
  for (const pid of orphans) {
    killProcess(pid, 'SIGTERM');
  }

  // Schedule a SIGKILL pass for any that didn't exit. Don't block the
  // caller — this is purely cleanup hygiene. CodeRabbit on PR #774:
  // wrap the body so a throwing test seam (custom isAlive / killProcess)
  // doesn't escape as an uncaught exception. Production seams already
  // catch internally, so this is purely defence-in-depth for tests.
  setTimeout(() => {
    try {
      const stragglers = orphans.filter(isAlive);
      if (stragglers.length === 0) return;
      log(`[orphan-reaper] ${stragglers.length} orphan(s) survived SIGTERM; sending SIGKILL: ${stragglers.map(describeOrphan).join(', ')}`);
      for (const pid of stragglers) {
        killProcess(pid, 'SIGKILL');
      }
    } catch (err) {
      log(`[orphan-reaper] error in SIGKILL pass: ${(err as Error).message}`);
    }
  }, graceMs).unref();

  return orphans;
}
